import React, { useReducer } from 'react'
import { chunk } from 'lodash'
import { Button } from 'semantic-ui-react'

import withImmer from 'EditorApp/reducers/withImmer'
import apiRequest from 'utils/apiRequest'
import DocCardRow from 'components/DocCardRow'
import type { DocumentStatus } from 'EditorApp/types'

const CardsPerRow = 5

interface DocCardData {
  id: number
  isSelected: boolean
  status: DocumentStatus
}

interface DocCardCategory {
  readonly name: string
  readonly viewAllLink: string
  readonly documents: DocCardData[]
}

interface DocumentIndexState {
  selectedCount: number
  categories: DocCardCategory[]
}

interface Props {
  readonly rootPath: string
  readonly viewingSubCategory: boolean
  readonly categories: DocCardCategory[]
  readonly fontFamily: string
  readonly allowBulkDelete: boolean
  readonly allowBulkPublish: boolean
  readonly allowBulkDuplicate: boolean
}

type ChangeSelectAction = {
  type: 'CHANGE_SELECT'
  id: number
  isSelected: boolean
}
type ReducerFn = (
  state: DocumentIndexState,
  action: ChangeSelectAction
) => DocumentIndexState

const DocumentIndexReducer: ReducerFn = withImmer({
  CHANGE_SELECT: (state: DocumentIndexState, action: ChangeSelectAction) => {
    for (const [_id, category] of Object.entries(state.categories)) {
      for (const document of category.documents) {
        if (document.id === action.id) {
          document.isSelected = action.isSelected
        }
      }
    }

    const delta = action.isSelected ? 1 : -1
    state.selectedCount += delta
  },
})

const selectedDocumentIds = (state: DocumentIndexState) => {
  const documentIds = []

  for (const [_id, category] of Object.entries(state.categories)) {
    for (const document of category.documents) {
      if (document.isSelected) {
        documentIds.push(document.id)
      }
    }
  }

  return documentIds
}

const DocumentIndex = ({
  rootPath,
  viewingSubCategory,
  categories,
  fontFamily,
  allowBulkDelete,
  allowBulkPublish,
  allowBulkDuplicate,
}: Props) => {
  const HeadingTag = viewingSubCategory ? 'h4' : 'h1'

  const initialState: DocumentIndexState = { selectedCount: 0, categories }
  const [state, dispatch] = useReducer(DocumentIndexReducer, initialState)

  const allowBulkEdit =
    allowBulkDelete || allowBulkPublish || allowBulkDuplicate

  const handleSelectChange = allowBulkEdit
    ? (id: number, isSelected: boolean) => {
        dispatch({ type: 'CHANGE_SELECT', id, isSelected })
      }
    : null

  const submitDelete = () => {
    if (!window.confirm('Are you sure?')) return

    const selectedIds = selectedDocumentIds(state)

    apiRequest({
      method: 'DELETE',
      url: `${rootPath}/${selectedIds.join(',')}`,
      payload: { _method: 'delete' },
      requestDescription: `bulk delete`,
      reloadOnAuthError: false,
    })
  }

  const submitDuplicate = () => {
    const selectedIds = selectedDocumentIds(state)

    window.location.href = `${rootPath}/${selectedIds.join(',')}/duplicate/new`
  }

  const updatePublishedStatus = (status: DocumentStatus) => {
    if (!window.confirm('Are you sure?')) return

    const selectedIds = selectedDocumentIds(state)

    apiRequest({
      method: 'PATCH',
      url: `${rootPath}/${selectedIds.join(',')}/status`,
      payload: { status: status },
      requestDescription: `bulk publish/unpublish: ${status}`,
      reloadOnAuthError: false,
    })
  }

  const renderDocumentRows = (documents: DocCardData[]) => {
    if (documents.length <= 0) return 'no items found'

    return chunk(documents, CardsPerRow).map((rowDocuments, index) => (
      <DocCardRow
        key={index}
        cardsData={rowDocuments}
        cardsInRow={CardsPerRow}
        fontFamily={fontFamily}
        spacing={24}
        onCardSelectChange={handleSelectChange}
      />
    ))
  }

  const selectedDocumentStates = state.categories.reduce(
    (statuses, category) => {
      return category.documents.reduce((statuses, doc) => {
        if (doc.isSelected) statuses[doc.status] += 1
        return statuses
      }, statuses)
    },
    { personal: 0, published: 0, deleted: 0 }
  )

  const allowPublish = allowBulkPublish && selectedDocumentStates.personal > 0
  const allowUnpublish =
    allowBulkPublish && selectedDocumentStates.published > 0

  return (
    <>
      {state.selectedCount > 0 && (
        <div className="DocumentIndex__toolbar">
          {state.selectedCount} selected{' '}
          {allowBulkDelete && <Button onClick={submitDelete}>delete</Button>}
          {allowPublish && (
            <Button onClick={() => updatePublishedStatus('published')}>
              publish
            </Button>
          )}
          {allowUnpublish && (
            <Button onClick={() => updatePublishedStatus('personal')}>
              unpublish
            </Button>
          )}
          {allowBulkDuplicate && (
            <Button onClick={() => submitDuplicate()}>duplicate</Button>
          )}
        </div>
      )}

      {state.categories.map((category, index) => (
        <section key={index} className="DocumentIndex__category">
          <HeadingTag className="DocumentIndex__heading">
            <span>{category.name}</span>

            {!viewingSubCategory &&
              category.documents.length == CardsPerRow && (
                <a
                  href={category.viewAllLink}
                  className="DocumentIndex__link-all"
                >
                  see all
                </a>
              )}
          </HeadingTag>

          <div className="DocumentIndex__previews">
            {renderDocumentRows(category.documents)}
          </div>
        </section>
      ))}
    </>
  )
}

export default DocumentIndex
